/* The Yaupon Tea editorial search v11.27 */
(() => {
  const SELECTOR = '[data-yaupon-search]';

  const debounce = (fn, wait = 260) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const stripHtml = (value = '') => {
    const template = document.createElement('template');
    template.innerHTML = value;
    return (template.content.textContent || '').trim();
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const buildResult = (article) => {
    const link = document.createElement('a');
    link.className = 'yt27-search-result';
    link.href = article.url;
    link.setAttribute('role', 'listitem');

    if (article.image) {
      const image = document.createElement('img');
      image.className = 'yt27-search-result__image';
      image.src = article.image;
      image.alt = '';
      image.loading = 'lazy';
      link.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'yt27-search-result__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = 'YT';
      link.appendChild(placeholder);
    }

    const copy = document.createElement('span');
    const title = document.createElement('span');
    title.className = 'yt27-search-result__title';
    title.textContent = article.title;
    copy.appendChild(title);

    const meta = document.createElement('span');
    meta.className = 'yt27-search-result__meta';
    const summary = stripHtml(article.summary_html || '').split(/\s+/).slice(0, 12).join(' ');
    meta.textContent = [formatDate(article.published_at), summary].filter(Boolean).join(' — ');
    copy.appendChild(meta);

    link.appendChild(copy);
    return link;
  };

  const initSearch = (root) => {
    if (!root || root.dataset.yauponSearchReady === 'true') return;

    const input = root.querySelector('[data-yaupon-search-input]');
    const results = root.querySelector('[data-yaupon-search-results]');
    const form = root.querySelector('form');
    if (!input || !results || !form) return;

    root.dataset.yauponSearchReady = 'true';
    let controller;

    const setExpanded = (expanded) => {
      input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    const close = () => {
      results.hidden = true;
      results.replaceChildren();
      setExpanded(false);
    };

    const showMessage = (message) => {
      results.replaceChildren();
      const status = document.createElement('div');
      status.className = 'yt27-search-result--footer';
      status.setAttribute('role', 'status');
      status.textContent = message;
      results.appendChild(status);
      results.hidden = false;
      setExpanded(true);
    };

    const search = debounce(async () => {
      const query = input.value.trim();
      if (query.length < 2) {
        close();
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();
      showMessage('Searching The Yaupon Tea…');

      const rootUrl = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'all',
        'resources[options][fields]': 'title,body,tag,author'
      });

      try {
        const response = await fetch(`${rootUrl}search/suggest.json?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

        const data = await response.json();
        const articles = data?.resources?.results?.articles || [];
        results.replaceChildren();

        if (!articles.length) {
          showMessage(`No article suggestions for “${query}”. Press Search for the full results page.`);
          return;
        }

        articles.forEach((article) => results.appendChild(buildResult(article)));

        const footer = document.createElement('a');
        footer.className = 'yt27-search-result--footer';
        footer.href = `${rootUrl}search?q=${encodeURIComponent(query)}&type=article&view=yaupon-search`;
        const footerText = document.createElement('span');
        footerText.textContent = 'See all article results';
        const footerArrow = document.createElement('span');
        footerArrow.setAttribute('aria-hidden', 'true');
        footerArrow.textContent = '→';
        footer.append(footerText, footerArrow);
        results.appendChild(footer);
        results.hidden = false;
        setExpanded(true);
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    }, 260);

    input.addEventListener('input', search);
    input.addEventListener('focus', () => {
      if (results.children.length) {
        results.hidden = false;
        setExpanded(true);
      }
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) close();
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initSearch);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
